import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    useSwitcherClient,
    useSwitcherClientCore
} from "hooks/useSwitcherClient";
import { RootState } from "store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { ModalBase } from "../ModalBase";
import { useTranslation } from "react-i18next";
import { Wizard } from "components/wizards/Wizard";
import { SelectPlanStep } from "./steps/SelectPlanStep";
import { CheckoutStep } from "./steps/CheckoutStep";
import { PauseStep } from "./steps/PauseStep";
import {
    CouponResponse,
    GetInvoiceResponse,
    SilverSunnStripeCreditCard,
    SilverSunnStripeCustomer,
    SilverSunnStripeSubscription,
    StripeInvoiceBindingModel,
    StripePrice,
    StripePriceAnonymous
} from "@switcherstudio/switcher-api-client";
import { setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { exists } from "helpers/booleans";
import { useGetLastClaimedCoupon } from "hooks/useGetLastClaimedCoupon";
import { useRunOnce } from "hooks/useRunOnce";
import { useSettableMemo } from "hooks/useSettableMemo";
import { useCheckout } from "hooks/useCheckout";
import rollbar from "helpers/rollbar";
import { StripeError } from "@stripe/stripe-js";
import { PauseAction, usePause } from "hooks/usePause";
import { usePauseStatus } from "hooks/usePauseStatus";
import dayjs from "dayjs";
import { useCancelSubscription } from "hooks/useCancelSubscription";
import { useHasPlanRole } from "hooks/useHasPlanRole";
import { displayAmount } from "helpers/stripe";
import { AttentionModal } from "../AttentionModal";

const pausedProductId = import.meta.env.VITE_PAUSED_PRODUCT_ID;

export enum ManagePlanModalSteps {
    SelectPlan = "select-plan",
    Checkout = "checkout",
    Pause = "pause"
}

export interface ManagePlanModalProps {
    prices: StripePrice[];
    preselectedPrice?: StripePrice | StripePriceAnonymous;
    couponCode?: string;
    stripePricesLoading: boolean;
    customer: SilverSunnStripeCustomer;
    licensedSubscription: SilverSunnStripeSubscription;
    lastAttemptedInvoice: StripeInvoiceBindingModel | GetInvoiceResponse;
    isPastDue: boolean;
    onCheckoutSuccess: (args?: {
        message?: string;
        selectedPaymentMethodId: string;
    }) => void;
    onCheckoutError: (
        message: string,
        args: { selectedPaymentMethodId: string; stripeError?: StripeError }
    ) => void;
    onPause: () => void;
    onPauseUpdate: () => void;
    onCancelPause?: (pauseAction: PauseAction) => void;
    onCancelPlan: () => Promise<void>;
    onPauseError: (error: any, pauseAction: PauseAction) => void;
}

export const ManagePlanModal = ({
    prices,
    preselectedPrice,
    couponCode,
    stripePricesLoading,
    customer,
    licensedSubscription,
    isPastDue,
    lastAttemptedInvoice,
    onCheckoutSuccess,
    onCheckoutError,
    onPause,
    onPauseUpdate,
    onCancelPause,
    onCancelPlan,
    onPauseError
}: ManagePlanModalProps) => {
    const { userInfo } = useSelector((s: RootState) => s.user);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [selectedPlan, setSelectedPlan] = useState<
        StripePrice | StripePriceAnonymous | undefined
    >(
        isPastDue
            ? prices?.find((p) => p.Id === licensedSubscription?.PlanId)
            : undefined
    );

    /** if the currentPlan !isPublic, it won't be selected in the modal */
    const currentPlan = useMemo<StripePrice>(
        () =>
            prices?.filter(
                (price) => price.Id === userInfo?.Plan?.Details?.Id
            )?.[0],
        [prices, userInfo?.Plan?.Details?.Id]
    );
    const [localSelectedPlan, setLocalSelectedPlan] = useState<
        StripePrice | StripePriceAnonymous | undefined
    >(currentPlan);

    const [paymentMethodReady, setPaymentMethodReady] =
        useState<boolean>(false);

    const [invoice, setInvoice] = useState<
        StripeInvoiceBindingModel | GetInvoiceResponse
    >(lastAttemptedInvoice);

    const { userIsOrWillPause } = usePauseStatus();

    const [isPausedSelected, setIsPausedSelected] = useState<boolean>(false);
    const [pauseDate, setPauseDate] = useState<string | null>(null);

    const activeStep = useMemo<ManagePlanModalSteps>(() => {
        if (userIsOrWillPause || isPausedSelected) {
            return ManagePlanModalSteps.Pause;
        }

        return !selectedPlan
            ? ManagePlanModalSteps.SelectPlan
            : ManagePlanModalSteps.Checkout;
    }, [userIsOrWillPause, isPausedSelected, selectedPlan]);

    useEffect(() => {
        if (preselectedPrice) {
            /** if the url contains a planId, this takes the user directly to the checkout step */
            setSelectedPlan(preselectedPrice);
            /** enables the Subscribe button */
            setLocalSelectedPlan(preselectedPrice);
        }
    }, [preselectedPrice]);

    const [addingNewPaymentMethod, setAddingNewPaymentMethod] =
        useState<boolean>(false);

    const { data: proration, dispatchApiRequest: getProrationAmounts } =
        useSwitcherClient((client) => client.stripe_GetProration, {
            requestImmediately: false,
            onError: (e) => rollbar.error("Error getting proration amounts", e)
        });

    const { dispatchApiRequest: postInvoice } = useSwitcherClient(
        (client) => client.stripeInvoices_PostInvoice,
        {
            requestImmediately: false
        }
    );

    const {
        data: subscriptionSchedule,
        dispatchApiRequest: getSubscriptionShedule
    } = useSwitcherClientCore(
        (client) => client.stripe_GetScheduleBySubscriptionId,
        {
            requestImmediately: false,
            args: [licensedSubscription?.SilverSunnStripeSubscriptionId]
        }
    );
    // Fetch subscription schedule when the user is paused or will pause
    useEffect(() => {
        if (userIsOrWillPause) {
            getSubscriptionShedule();
        }
    }, [userIsOrWillPause, getSubscriptionShedule]);

    const {
        claimedCoupon,
        addClaimedCoupon,
        removeClaimedCoupon,
        loading: loadingCoupon,
        error: couponError
    } = useGetLastClaimedCoupon({
        planId: selectedPlan?.Id,
        onChange: (claimedCoupon) =>
            !lastAttemptedInvoice &&
            !!selectedPlan?.Id &&
            getProrationOrInvoice(claimedCoupon)
    });

    const isLoadingInvoiceRef = useRef(false);
    const getProrationOrInvoice = useCallback(
        async (_claimedCoupon: CouponResponse) => {
            if (selectedPlan.IsRecurring) {
                await getProrationAmounts([
                    selectedPlan?.Id,
                    1,
                    customer?.StripeSubscriptions?.[0]
                        ?.SilverSunnStripeSubscriptionId,
                    _claimedCoupon?.ResellerInventoryItem.Id
                ]);
            } else {
                // Prevent concurrent calls with a simple loading check
                if (isLoadingInvoiceRef.current) return;

                try {
                    isLoadingInvoiceRef.current = true;
                    setInvoice(
                        await postInvoice([
                            userInfo?.User?.Details?.Id,
                            {
                                InvoiceItems: [{ Price: selectedPlan?.Id }],
                                ResellerInventoryItemId:
                                    _claimedCoupon?.ResellerInventoryItem.Id
                            }
                        ])
                    );
                } catch (error) {
                    rollbar.error("Error creating invoice", error, {
                        error,
                        planId: selectedPlan?.Id,
                        userId: userInfo?.User?.Details?.Id,
                        isRecurring: selectedPlan?.IsRecurring,
                        hasCoupon: !!_claimedCoupon
                    });
                } finally {
                    isLoadingInvoiceRef.current = false;
                }
            }
        },
        [
            getProrationAmounts,
            postInvoice,
            customer?.StripeSubscriptions,
            selectedPlan,
            userInfo?.User?.Details?.Id
        ]
    );

    const paymentMethodIsRequired = useMemo<boolean>(
        () =>
            proration?.AmountDue > 0 ||
            (claimedCoupon?.ResellerInventoryItem?.ResellerInventory
                ?.IsPaymentMethodRequired ??
                true),
        [claimedCoupon, proration]
    );

    const [selectedPaymentMethod, setSelectedPaymentMethod] =
        useSettableMemo<SilverSunnStripeCreditCard>(
            () =>
                paymentMethodIsRequired
                    ? customer?.StripeCreditCards.find((cc) => cc.Default)
                    : null,
            [customer]
        );

    useRunOnce(() => {
        setPaymentMethodReady(true);
    }, exists(selectedPaymentMethod) || !paymentMethodIsRequired);

    const { reset } = useRunOnce(async () => {
        if (!isPastDue) {
            await getProrationOrInvoice(claimedCoupon);
        }
    }, exists(customer) && exists(selectedPlan));

    const checkCouponToRemove = useCallback(() => {
        if (!!claimedCoupon?.ToPlan) {
            removeClaimedCoupon();
        }
    }, [claimedCoupon, removeClaimedCoupon]);

    const close = useCallback(() => {
        checkCouponToRemove();
        setIsPausedSelected(false);
        setSelectedPlan(undefined);
        dispatch(
            setActiveModal({
                id: Modals.None,
                type: Modals.None
            })
        );
    }, [dispatch, checkCouponToRemove]);

    const successDisabled = useMemo<boolean>(() => {
        if (loadingCoupon) return true;

        switch (activeStep) {
            case ManagePlanModalSteps.SelectPlan:
                // There is no selected plan, then is disabled
                if (localSelectedPlan === undefined) return true;
                // Otherwise, if the plan is cancelled or the user is trialing
                else if (
                    userInfo?.Plan?.Details?.CancelAtPeriodEnd ||
                    userInfo?.Plan?.Details?.Status === "trialing" ||
                    couponCode
                ) {
                    // They can select whichever plan they want
                    return false;
                } else {
                    // Otherwise they can't select the plan they are currently on
                    return (
                        localSelectedPlan?.Id === currentPlan?.Id &&
                        userInfo?.Plan?.Details?.Status === "active"
                    );
                }
            case ManagePlanModalSteps.Checkout:
                if (!paymentMethodIsRequired) return false;
                return !selectedPaymentMethod && !paymentMethodReady;
            case ManagePlanModalSteps.Pause:
                if (!pauseDate) return true;

                if (
                    dayjs(pauseDate).isSame(
                        dayjs(userInfo.Plan?.Schedule?.Details?.PausedEndDate),
                        "date"
                    )
                ) {
                    // If the pause date is the same as the user's current pause date, then it's disabled
                    return true;
                }

                // If the current utc date plus 2 hours is after the active until, then it is disabled
                if (
                    dayjs()
                        .add(2, "hours")
                        .isAfter(dayjs(userInfo.Plan?.Details?.ActiveUntil))
                ) {
                    return true;
                }
        }
    }, [
        activeStep,
        couponCode,
        currentPlan?.Id,
        loadingCoupon,
        localSelectedPlan,
        paymentMethodIsRequired,
        paymentMethodReady,
        selectedPaymentMethod,
        userInfo,
        pauseDate
    ]);

    /** Call handleSubmit function from child component */
    const handleSelectPlanSubmit = useCallback(
        (onHandleSubmit: () => void) => {
            /** !stripePricesLoading stops this from running and throwing errors on initial render */
            if (!stripePricesLoading) {
                onHandleSubmit();
            }
        },
        [stripePricesLoading]
    );

    const { checkout, isSubmitting } = useCheckout({
        licensedSubscription,
        resellerInventoryItemId: claimedCoupon?.ResellerInventoryItem?.Id,
        plan: selectedPlan,
        isNewSubscription: !licensedSubscription,
        isTrialing: userInfo?.Plan?.Details?.Status === "trialing",
        lastAttemptedInvoice,
        onSuccess: ({ selectedPaymentMethodId }) => {
            close();
            onCheckoutSuccess({ selectedPaymentMethodId });
        },
        onError: (error, args) => {
            reset();
            onCheckoutError(error, args);
        }
    });

    const { data: pausedPrices } = useSwitcherClient(
        (client) => client.stripePrices_GetPricesByProductId,
        {
            args: [pausedProductId],
            requestImmediately: true
        }
    );

    const { createPause, updatePause, cancelPause, cancelPlan, loading } =
        usePause({
            currentSubscription: licensedSubscription,
            subscriptionSchedule,
            planId: pausedPrices?.Prices?.find(
                (p) => p?.Details?.Active && p?.Details.Amount === 0
            )?.Details?.Id,
            pauseDate,
            onPause: () => {
                close();
                onPause();
            },
            onPauseUpdate: () => {
                close();
                onPauseUpdate();
            },
            onCancelPause: (pauseAction: PauseAction) => {
                close();
                onCancelPause?.(pauseAction);
            },
            onCancelPlan: () => {
                close();
                onCancelPlan?.();
            },
            onError: (error, pauseAction: PauseAction) => {
                onPauseError(error, pauseAction);
            }
        });

    const isGrowUser = useHasPlanRole("Grow");
    const [attentionModalIsOpen, setAttentionModalIsOpen] = useState(false);

    const subscriptionId = useMemo(
        () =>
            customer?.StripeSubscriptions?.[0]?.SilverSunnStripeSubscriptionId,
        [customer]
    );

    const currentSubscriptionCost = useMemo(
        () => customer?.StripeSubscriptions?.[0]?.Amount ?? 0,
        [customer]
    );

    const { initiateProfitwellRetainAttempt } = useCancelSubscription(
        licensedSubscription?.SilverSunnStripeSubscriptionId,
        customer?.SilverSunnStripeCustomerId,
        {
            onBeforeConfirm: close,
            onCancel: onCancelPlan,
            overrideCancel: userIsOrWillPause ? cancelPlan : null
        }
    );

    // Calculate the price of the lowest cost available plan
    // that matches the user's current plan's recurring interval
    const priceOfLowestCostAvailablePlan = useMemo(() => {
        const filteredPlans = prices?.filter(
            (p) =>
                p?.RecurringInterval ===
                userInfo?.Plan?.Details?.RecurringInterval
        );
        if (!filteredPlans || filteredPlans.length === 0) return undefined;
        return filteredPlans.reduce(
            (min, plan) => (plan?.Amount < min ? plan?.Amount : min),
            filteredPlans[0]?.Amount
        );
    }, [prices, userInfo?.Plan?.Details?.RecurringInterval]);

    const formattedLowAvailableCost = displayAmount(
        priceOfLowestCostAvailablePlan,
        {
            signed: true,
            compact: true,
            roundUp: false
        }
    );

    const deleteSubAndCloseModal = useCallback(async () => {
        initiateProfitwellRetainAttempt(subscriptionId);
        close();
    }, [initiateProfitwellRetainAttempt, close, subscriptionId]);

    const openAttentionModalOrProfitwellSalvage = useCallback(async () => {
        const userIsOnLegacyStudioPlan =
            !isGrowUser &&
            currentSubscriptionCost < priceOfLowestCostAvailablePlan;
        if (userIsOnLegacyStudioPlan) {
            setAttentionModalIsOpen(true);
        } else {
            deleteSubAndCloseModal();
        }
    }, [
        isGrowUser,
        priceOfLowestCostAvailablePlan,
        currentSubscriptionCost,
        deleteSubAndCloseModal
    ]);

    const onPauseSelection = useCallback(() => {
        setIsPausedSelected(true);
    }, []);
    const modalTitle = useMemo<string>(() => {
        return isPausedSelected
            ? t("subscription:pause-plan")
            : t("subscription:manage-plan");
    }, [t, isPausedSelected]);

    const modalSuccessButtonText = useMemo<string>(() => {
        if (activeStep === ManagePlanModalSteps.Pause) {
            if (userIsOrWillPause) {
                return t("buttons:update");
            }
            return t("buttons:pause");
        }

        if (activeStep === ManagePlanModalSteps.SelectPlan)
            return t("buttons:update");

        return t("buttons:subscribe");
    }, [t, activeStep, userIsOrWillPause]);

    const modalSuccessHandler = useCallback(() => {
        if (activeStep === ManagePlanModalSteps.SelectPlan) {
            setSelectedPlan(localSelectedPlan);
            return;
        }

        if (activeStep === ManagePlanModalSteps.Checkout) {
            checkout({
                selectedPaymentMethodId: addingNewPaymentMethod
                    ? "new-payment-method"
                    : selectedPaymentMethod?.SilverSunnStripeCreditCardId,
                invoiceId: invoice?.id,
                planId: selectedPlan?.Id
            });
            return;
        }

        if (activeStep === ManagePlanModalSteps.Pause) {
            if (userIsOrWillPause) {
                updatePause();
                return;
            }
            createPause();
            return;
        }
    }, [
        activeStep,
        addingNewPaymentMethod,
        checkout,
        updatePause,
        createPause,
        invoice,
        localSelectedPlan,
        selectedPaymentMethod,
        selectedPlan,
        userIsOrWillPause
    ]);

    return (
        <ModalBase
            isOpen
            header={modalTitle}
            onSuccess={modalSuccessHandler}
            successDisabled={successDisabled}
            successButton={modalSuccessButtonText}
            shouldNotCloseOnSuccess={true}
            onDismiss={close}
            dismissButton={t("buttons:cancel")}
            loading={isSubmitting || loading}
            detect3DSecureModal
        >
            <div>
                <Wizard<ManagePlanModalSteps>
                    activeStep={activeStep}
                    steps={[
                        {
                            id: ManagePlanModalSteps.SelectPlan,
                            component: (
                                <SelectPlanStep
                                    loading={stripePricesLoading}
                                    prices={prices}
                                    currentPlan={userInfo?.Plan?.Details?.Id}
                                    selectPlan={(plan) => {
                                        setLocalSelectedPlan(plan);
                                    }}
                                    onHandleSubmit={handleSelectPlanSubmit}
                                    onCancel={
                                        openAttentionModalOrProfitwellSalvage
                                    }
                                    onPause={onPauseSelection}
                                />
                            )
                        },
                        {
                            id: ManagePlanModalSteps.Checkout,
                            component: (
                                <CheckoutStep
                                    selectedPlan={selectedPlan}
                                    unselectPlan={() => {
                                        checkCouponToRemove();
                                        reset();
                                        setSelectedPlan(undefined);
                                    }}
                                    customer={customer}
                                    proration={proration}
                                    invoice={invoice}
                                    isPastDue={isPastDue}
                                    claimedCoupon={claimedCoupon}
                                    loadingCoupon={loadingCoupon}
                                    addingNewPaymentMethod={
                                        addingNewPaymentMethod
                                    }
                                    setAddingNewPaymentMethod={
                                        setAddingNewPaymentMethod
                                    }
                                    selectedPaymentMethod={
                                        selectedPaymentMethod
                                    }
                                    setSelectedPaymentMethod={
                                        setSelectedPaymentMethod
                                    }
                                    couponError={couponError}
                                    addClaimedCoupon={addClaimedCoupon}
                                    removeClaimedCoupon={removeClaimedCoupon}
                                    paymentMethodIsRequired={
                                        paymentMethodIsRequired
                                    }
                                    onPaymentMethodChange={(id, event) => {
                                        if (id !== "new-payment-method") {
                                            setPaymentMethodReady(true);
                                        } else {
                                            setPaymentMethodReady(
                                                event.complete
                                            );
                                        }
                                    }}
                                />
                            )
                        },
                        {
                            id: ManagePlanModalSteps.Pause,
                            component: (
                                <PauseStep
                                    currentSubscription={licensedSubscription}
                                    pauseDate={pauseDate}
                                    setPauseDate={setPauseDate}
                                    cancelPause={cancelPause}
                                    cancelPlan={
                                        openAttentionModalOrProfitwellSalvage
                                    }
                                />
                            )
                        }
                    ]}
                />
                <AttentionModal
                    isOpen={attentionModalIsOpen}
                    setIsOpen={setAttentionModalIsOpen}
                    continueText={t("subscription:cancel-plan")}
                    handleContinue={() => {
                        deleteSubAndCloseModal();
                    }}
                    handleCancel={() => setAttentionModalIsOpen(false)}
                >
                    {t("subscription:cancel-legacy-studio-sub-warning", {
                        formattedLowAvailableCost,
                        interval: userInfo?.Plan?.Details?.RecurringInterval
                    })}
                </AttentionModal>
            </div>
        </ModalBase>
    );
};
